/**
 * Single source of truth for image-upload validation across the app.
 *
 * Every image upload (profile avatar, profile hero/cover, market proposal cover,
 * outcome thumbnails, future surfaces) flows through `<CropModal>` which reads
 * these constants. The `<ImageUploadDropzone>` shared dropzone and the
 * `<CropModal>` validation BOTH consume this — there must be no second copy
 * of these limits anywhere in `frontend/src/`.
 */

export const IMAGE_UPLOAD_MAX_BYTES: number = 5 * 1024 * 1024; // 5 MB
export const IMAGE_UPLOAD_MAX_MB: number = IMAGE_UPLOAD_MAX_BYTES / (1024 * 1024);

export const IMAGE_UPLOAD_ALLOWED_TYPES: ReadonlyArray<string> = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

/** `accept` attribute for `<input type="file">` — comma-separated MIME list. */
export const IMAGE_UPLOAD_ACCEPT_ATTR: string = IMAGE_UPLOAD_ALLOWED_TYPES.join(',');

export type ImageUploadValidationError = 'invalid_type' | 'too_large';

/** Validates a `File` against the shared limits. Returns null when the file is OK. */
export function validateImageUpload(file: File): ImageUploadValidationError | null {
  if (!IMAGE_UPLOAD_ALLOWED_TYPES.includes(file.type)) return 'invalid_type';
  if (file.size > IMAGE_UPLOAD_MAX_BYTES) return 'too_large';
  return null;
}
